import { useEffect, useState } from "react";
import {
  Copy01Icon,
  PackageAddIcon,
  Tick01Icon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";

import type { Membership } from "@/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { agentConnectionPrompt } from "@/lib/agent-connection-prompt";

const SKILL_INSTALL_COMMAND = "npx skills add plannotator/appnotes-skill";

/** Agent-setup surface displayed by the reusable sidebar dialog. */
export type AgentSetupDialogKind = "install" | "instructions";

const AGENT_BRANDS = ["claude", "codex", "cursor", "copilot"] as const;

/** Render the same coding-agent mark cluster used by Wrangler's handoff page. */
export function AgentBrandIcons({
  className,
}: {
  readonly className?: string;
}) {
  return (
    <span aria-hidden="true" className={cn("agent-brand-icons", className)}>
      {AGENT_BRANDS.map((brand) => (
        <span className="agent-brand-icon" key={brand}>
          <img
            alt=""
            className="agent-brand-icon-light"
            draggable={false}
            src={`/icons/agents/${brand}/light.svg`}
          />
          <img
            alt=""
            className="agent-brand-icon-dark"
            draggable={false}
            src={`/icons/agents/${brand}/dark.svg`}
          />
        </span>
      ))}
    </span>
  );
}

/** Render the larger skill-install or agent-instruction handoff. */
export function AgentSetupDialog({
  apiOrigin,
  kind,
  membership,
  mcpAvailable,
  onOpenChange,
}: {
  readonly apiOrigin: string;
  readonly kind: AgentSetupDialogKind | null;
  readonly membership: Membership;
  readonly mcpAvailable: boolean;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const prompt = agentConnectionPrompt({ apiOrigin, membership, mcpAvailable });
  return (
    <Dialog open={kind !== null} onOpenChange={onOpenChange}>
      <DialogContent className="agent-setup-dialog sm:max-w-xl">
        {kind === "install" ? (
          <>
            <DialogHeader>
              <DialogTitle>Install the AppNotes skill</DialogTitle>
              <DialogDescription>
                Add the AppNotes workflow to Codex or another compatible coding
                agent.
              </DialogDescription>
            </DialogHeader>
            <div className="agent-command-block">
              <HugeiconsIcon icon={PackageAddIcon} strokeWidth={1.8} />
              <code>{SKILL_INSTALL_COMMAND}</code>
            </div>
            <p className="agent-setup-note">
              Run this once in your project terminal. Then copy the Agent
              instructions when you want the agent to connect a web app.
            </p>
            <DialogFooter>
              <CopyButton label="Copy command" value={SKILL_INSTALL_COMMAND} />
            </DialogFooter>
          </>
        ) : kind === "instructions" ? (
          <>
            <DialogHeader>
              <div className="agent-setup-title-row">
                <DialogTitle>Connect with your coding agent</DialogTitle>
                <AgentBrandIcons />
              </div>
              <DialogDescription>
                Copy this into Codex. The agent can register the Site and wire
                up the SDK for {membership.organization_name}.
              </DialogDescription>
            </DialogHeader>
            <pre className="agent-prompt-block">
              <code>{prompt}</code>
            </pre>
            <p className="agent-setup-note">
              {mcpAvailable ? (
                <>
                  Sign in through your agent, then approve its team and access
                  in AppNotes. No API key is needed. The prompt contains no
                  secret.
                </>
              ) : (
                <>
                  The prompt contains no secret. Your agent uses the
                  organization API key from <code>APPNOTES_API_KEY</code> and
                  asks you when it is missing.
                </>
              )}
            </p>
            <DialogFooter>
              <CopyButton label="Copy prompt" value={prompt} />
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/** Render the compact agent shortcut inside the manual Site wizard. */
export function AgentPromptShortcut({
  apiOrigin,
  membership,
  mcpAvailable,
}: {
  readonly apiOrigin: string;
  readonly membership: Membership;
  readonly mcpAvailable: boolean;
}) {
  const prompt = agentConnectionPrompt({ apiOrigin, membership, mcpAvailable });
  return (
    <section className="agent-prompt-shortcut">
      <div>
        <div className="agent-prompt-shortcut-heading">
          <strong>Want your coding agent to set it up?</strong>
          <AgentBrandIcons />
        </div>
        <p>
          Copy one instruction and let Codex register and connect this site.
        </p>
      </div>
      <CopyButton label="Copy agent prompt" size="sm" value={prompt} />
    </section>
  );
}

function CopyButton({
  label,
  size = "default",
  value,
}: {
  readonly label: string;
  readonly size?: "default" | "sm";
  readonly value: string;
}) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");

  useEffect(() => {
    if (state === "idle") return;
    const timeout = window.setTimeout(() => setState("idle"), 1_500);
    return () => window.clearTimeout(timeout);
  }, [state]);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setState("copied");
    } catch {
      setState("failed");
    }
  }

  return (
    <Button
      aria-live="polite"
      onClick={() => void copy()}
      size={size}
      type="button"
      variant="outline"
    >
      <HugeiconsIcon
        data-icon="inline-start"
        icon={state === "copied" ? Tick01Icon : Copy01Icon}
        strokeWidth={1.8}
      />
      {state === "copied"
        ? "Copied"
        : state === "failed"
          ? "Copy failed"
          : label}
    </Button>
  );
}
